/** @jsx jsx */

import { ChangeEvent, useState, Fragment } from 'react';

import { jsx } from '@emotion/core';
import {
  Field,
  FormLabel,
  LabeledCheckbox,
  Spacer,
  TextInput,
} from '@zapier/design-system';

import { createChangelogString } from 'app/developer-v3/utils';

import VersionModalWrapper from './VersionModalWrapper';

type Props = {
  isSaving: boolean;
  onClose: () => void;
  onConfirm: (changeLogString: string) => void;
};

const Promote = (props: Props) => {
  const { isSaving, onClose, onConfirm } = props;

  const [description, setDescription] = useState('');
  const [hasBugFixes, setHasBugFixes] = useState(false);
  const [hasNewFeatures, setHasNewFeatures] = useState(false);

  return (
    <VersionModalWrapper
      confirmButtonDisabled={!hasBugFixes && !hasNewFeatures}
      confirmButtonLabel="Promote"
      description="Please provide the Zapier team and your end users with the reason for your integration updates."
      heading="Promote your Integration"
      isSaving={isSaving}
      onClose={onClose}
      onConfirm={() => {
        const changeLogString = createChangelogString({
          description,
          reasons: { hasBugFixes, hasNewFeatures },
        });
        onConfirm(changeLogString);
      }}
    >
      <Field
        isRequired={true}
        label="Reason(s) for change"
        renderInput={inputProps => (
          <Fragment>
            <FormLabel isSelected={hasNewFeatures}>
              <LabeledCheckbox
                aria-label="Has new features"
                checked={hasNewFeatures}
                name="checkbox"
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setHasNewFeatures(event.currentTarget.checked);
                }}
                tabIndex={0}
                value="hasNewFeatures"
                {...inputProps}
              >
                New Features
              </LabeledCheckbox>
            </FormLabel>
            <FormLabel isSelected={hasBugFixes}>
              <LabeledCheckbox
                aria-label="Has bug fixes"
                checked={hasBugFixes}
                name="checkbox"
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setHasBugFixes(event.currentTarget.checked);
                }}
                tabIndex={0}
                value="hasBugFixes"
              >
                Bug Fixes
              </LabeledCheckbox>
            </FormLabel>
          </Fragment>
        )}
      />
      <Spacer height={30} />
      <Field
        label="User-facing description"
        renderHelpText={() => (
          <p>
            How does this update help your users? The clearer your description,
            the more likely Zapier’s marketing team is to highlight your update!
            (max. 255 chars)
          </p>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="User-facing description"
            isMultiline={true}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setDescription(event.currentTarget.value);
            }}
            rows={5}
          />
        )}
      />
      <Spacer height={40} />
    </VersionModalWrapper>
  );
};

export default Promote;
